import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UserService } from '@bo-client/core/api/api';
import { ToastrService } from 'ngx-toastr';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CreateOrUpdateUserDto } from '@bo-client/shared/models/createOrUpdateUserDto';
import { TenantDto } from '@bo-client/shared/models/tenantDto';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { ContactData } from '@bo-client/shared/models/contactData';
import { AuthorizationService } from '@bo-client/core/authorization/authorization.service';
import { UserDto } from '@bo-client/shared/models/userDto';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

@Component({
  selector: 'app-edit-personal-data-modal',
  templateUrl: './edit-personal-data-modal.component.html',
  styleUrls: ['./edit-personal-data-modal.component.scss']
})
export class EditPersonalDataModalComponent implements OnInit, OnDestroy {
  userForm: any;
  model: CreateOrUpdateUserDto = {} as CreateOrUpdateUserDto;
  allowedTenants: TenantDto[];
  isLoading = true;
  isSuperUser: boolean;
  public isInstallerUser = false;
  userData: UserDto;
  private compUnsubs$ = new Subject<void>();

  constructor(
    private authorizationService: AuthorizationService,
    private userService: UserService,
    private httpHapService: HttpHapService,
    private toastService: ToastrService,
    public dialogRef: MatDialogRef<EditPersonalDataModalComponent>,
    @Inject(MAT_DIALOG_DATA) public modalInput: UserDto,
    public dataShareService: DataShareService
  ) {
    //
  }

  ngOnInit(): void {
    // in case we have something passed by the caller of the modal
    if (this.modalInput) {
      this.userData = this.modalInput;
      this.handleNewModel(this.modalInput);
      // otherwise fall back to the current user
    } else {
      this.dataShareService.userData$.pipe(takeUntil(this.compUnsubs$)).subscribe((userData) => {
        this.handleNewModel(userData);
      });
    }
  }

  handleNewModel(userData: UserDto) {
    this.userData = userData;

    if (!this.userData?.contactData) {
      this.userData.contactData = { firstName: '', salutation: '0002' };
    }
    this.model.userId = this.userData.id;
    this.model.contactData = this.userData.contactData;
    this.model.externalId = this.userData.externalId;
    this.model.roles = this.userData.roles.sort((a, b) => a.id - b.id);
    this.isSuperUser = this.userData.role === 'ROLE_SUPERADMIN';
    this.allowedTenants = this.userData.allowedTenants;
    this.isInstallerUser = this.authorizationService.hasOneOfThisRoles([
      'ROLE_INSTALLATEUR',
      'ROLE_ADMIN_INSTALLATEUR'
    ]);
  }

  close() {
    this.dialogRef.close(false);
  }

  confirm(user: ContactData) {
    this.userService
      .updatePersonalDataAndPassword(this.model)
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe(
        () => {
          this.dialogRef.close(user);
        },
        (error) => {
          this.toastService.error(error);
        }
      );
  }

  /**
   * Comprehensive reset of backoffice settings:
   * 1. Reset server-side configurations
   * 2. Clear localStorage items related to UI state
   * 3. Reload the page to ensure all components pick up the reset configuration
   */
  resetBackofficeSettings() {
    // First reset the configuration through the service
    this.dataShareService.resetConfiguration();

    // Clear localStorage items that might store UI state
    localStorage.removeItem('columnState');
    localStorage.removeItem('filterModel');
    localStorage.removeItem('menu_expanded');

    // Show confirmation to the user
    this.toastService.success('Einstellungen wurden zurückgesetzt. Seite wird neu geladen...');

    // Close the dialog
    this.dialogRef.close();

    // Reload the page after a short delay to allow the user to see the toast message
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
